import React, { createContext, useState, useEffect, useContext, useMemo, useCallback, ReactNode } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUp,
  changePassword,
  forgotPassword,
  deleteUser,
} from '../services/auth';
import { storeCanvasCredentials, fetchCanvasCredentials } from '../services/canvasService';
import tokenManager from '../services/TokenManager';
import Loading from '../components/common/Loading';
import { purgeApiCacheViaWorker } from '../services/cachePurge';
import { DEMO_MODE_KEY } from '../data/mockData';
import {
  OFFLINE_MODE_KEY,
  clearSessionKeepingReturn,
  discardOfflineSessionUnlessOwner,
} from '../utils/offlineSession';
import type {
  AuthContextValue,
  AuthUser,
  CanvasUser,
  PasswordChallengeResponse,
  ProfileUpdateData,
  StoredCanvasCredentials,
} from '../types/auth';

interface CanvasState {
  canvasToken: string | null;
  // Always a string once it is in this shape -- see toCanvasUserId below.
  canvasUserId: string | null;
  canvasRootUrl: string | null;
  availableUsers: CanvasUser[];
}

// How long the mount-time session recovery waits before its one retry when the
// browser thinks it is online (when offline it waits for the `online` event).
const RETRY_DELAY_MS = 5000;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Server may return canvasUsers as a JSON string or an array.
const parseCanvasUsers = (raw: unknown): CanvasUser[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Task 5.2c: the ONE place the active Canvas identity's id is given its
// spelling. It arrives as a number from the Canvas API's observees response
// (availableUsers[].id) and as a string from sessionStorage, and it ends up
// as a TanStack Query key segment -- where ['courses', 'auth', 111] and
// ['courses', 'auth', '111'] are two distinct cache entries for one child.
// Two entries meant a duplicate fetch and, worse, a clearCache() (which
// removes by prefix, useCourseData.ts:333) that dropped only one of them,
// so an entry a user switch is supposed to clear could survive it.
//
// String is the direction chosen because it is what every rehydrated
// session already uses: sessionStorage.getItem() cannot return a number, so
// normalising the other way would have meant parsing every read and would
// have broken on any non-numeric id Canvas might issue.
//
// This is deliberately NOT applied at each query-key site. Doing it there
// leaves currentUser.canvasUserId inconsistent for every other reader (most
// importantly AppModeContext's getActiveCredentials(), which is what
// clearCache and the courseDetails/courseHistory keys are built from), and
// the next key added re-acquires the bug.
const toCanvasUserId = (id: unknown): string | null =>
  id === null || id === undefined || id === '' ? null : String(id);

const readCanvasFromSession = (): CanvasState => ({
  canvasToken: sessionStorage.getItem('canvasToken'),
  canvasUserId: sessionStorage.getItem('canvasUserId'),
  canvasRootUrl: sessionStorage.getItem('canvasRootUrl'),
  availableUsers: parseCanvasUsers(sessionStorage.getItem('availableUsers')),
});

const clearGuestAndDemoState = () => {
  sessionStorage.removeItem('guestCredentials');
  sessionStorage.removeItem('isGuestMode');
  sessionStorage.removeItem(DEMO_MODE_KEY);
};

// Persist a Cognito token bundle. The secure-storage encryption key is
// derived from the session identifier, so it MUST be set before any tokens
// are written.
const persistSession = async (
  tokens: { AccessToken?: string; IdToken?: string; RefreshToken?: string; tokenExpiry?: number },
  email: string
) => {
  tokenManager.setUserSession(email);
  const expiry = tokens.tokenExpiry || Date.now() + 3600 * 1000;
  await tokenManager.setTokenSecure('authToken', tokens.AccessToken);
  await tokenManager.setTokenSecure('idToken', tokens.IdToken);
  await tokenManager.setTokenSecure('refreshToken', tokens.RefreshToken);
  await tokenManager.setTokenSecure('tokenExpiry', expiry.toString());
  tokenManager.startMonitoring();
};

/**
 * Drop the service worker's cached API data (task 5.3c).
 *
 * Every sign-out path below funnels through here. Task 5.3a made course and
 * grade data persist on disk so the app works offline; clearing tokens and
 * sessionStorage does not touch it, so without this a signed-out family's
 * data stays on a shared device.
 *
 * Best-effort by construction: `purgeApiCacheViaWorker` resolves a result
 * object instead of rejecting, and the catch below covers the impossible
 * case. A cache that could not be cleared must never turn a successful
 * sign-out into a failed one -- the user would stay signed in, which is
 * strictly worse than the leftover it was trying to prevent.
 *
 * Idempotent: it deletes a cache by name, so running twice on one sign-out
 * (`forceLogout` also triggers the `force_logout` listener) is harmless.
 */
const purgeCachedUserData = async () => {
  try {
    const result = await purgeApiCacheViaWorker();
    if (!result.purged && result.reason !== 'no-worker') {
      console.warn('Cached API data may not have been cleared:', result.reason);
    }
  } catch (error) {
    console.error('Cache purge on sign-out failed:', error);
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [availableUsers, setAvailableUsers] = useState<CanvasUser[]>([]);
  const [loading, setLoading] = useState(true);
  // True while the offline-mode background retry of the session is pending.
  const [sessionRecovering, setSessionRecovering] = useState(false);

  // Subscribe to token manager events
  useEffect(() => {
    const unsubscribe = tokenManager.subscribe((event: string) => {
      // Both events mean this session's tokens are gone, so both purge.
      // Neither is a button press; `force_logout` in particular is the path
      // a shared-device user hits by walking away.
      //
      // `tokens_cleared` is the broader of the two, and it catches a
      // sign-out with nothing behind it at all: `TokenManager.refreshTokens`
      // clears the tokens and throws when a refresh fails, and the
      // mount-time auth check below just returns on that error. Nothing on
      // that path notifies `force_logout` -- only `TokenManager.forceLogout`
      // does -- so keying the purge on `force_logout` alone would leave the
      // data of a user whose session quietly expired sitting on the device.
      // Every caller of `clearTokens` is a sign-out, and the purge deletes a
      // cache by name, so overlapping with the paths that also purge
      // explicitly is harmless.
      //
      // Nothing awaits a subscriber, so this is started rather than awaited
      // -- the worker's `waitUntil` sees it through.
      if (event === 'force_logout' || event === 'tokens_cleared') {
        void purgeCachedUserData();
      }

      if (event === 'force_logout') {
        setCurrentUser(null);
        setAvailableUsers([]);
        // Task 6.0ag: not a bare clear(). The user is about to be sent to
        // /login and back, so the page they were on is remembered, and while
        // offline mode is on its keys (snapshot, child, owner email) stay for
        // the same-account comparison at sign-in. The API-cache purge above
        // is what removes the cached course data; everything else in
        // sessionStorage (Canvas token, guest state) is still cleared.
        clearSessionKeepingReturn();
      }
    });

    return unsubscribe;
  }, []);

  // Write server-fetched Canvas credentials into sessionStorage and state,
  // auto-selecting the default Canvas user when one is flagged.
  const applyCanvasCredentials = useCallback((credentials: StoredCanvasCredentials): CanvasState => {
    const users = parseCanvasUsers(credentials.canvasUsers);
    const defaultUser = users.find(user => user.default === true);

    sessionStorage.setItem('canvasToken', credentials.canvasToken);
    sessionStorage.setItem('availableUsers', JSON.stringify(users));
    if (credentials.canvasRootUrl) {
      sessionStorage.setItem('canvasRootUrl', credentials.canvasRootUrl);
    }
    if (defaultUser) {
      sessionStorage.setItem('canvasUserId', String(defaultUser.id));
    }

    setAvailableUsers(users);

    return {
      canvasToken: credentials.canvasToken,
      canvasUserId: defaultUser ? toCanvasUserId(defaultUser.id) : null,
      canvasRootUrl: credentials.canvasRootUrl || null,
      availableUsers: users,
    };
  }, []);

  // Fetch Canvas credentials from the server (single source of truth) and
  // hydrate local state. Returns null when none are stored or the fetch fails.
  const hydrateCanvasCredentials = useCallback(async (): Promise<CanvasState | null> => {
    try {
      const response = await fetchCanvasCredentials();
      const credentials = Array.isArray(response) ? response[0] : response;
      if (credentials?.canvasToken) {
        return applyCanvasCredentials(credentials);
      }
    } catch (error) {
      console.error('Failed to fetch Canvas credentials:', error);
    }
    return null;
  }, [applyCanvasCredentials]);

  // Check for an existing session on mount
  useEffect(() => {
    let cancelled = false;
    let cancelWait: (() => void) | null = null;

    // Resolves true when it is time for the one retry: on the `online` event
    // if the browser knows it is offline, otherwise after RETRY_DELAY_MS.
    // Resolves false if the provider unmounts first.
    const waitToRetry = () =>
      new Promise<boolean>(resolve => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const finish = (go: boolean) => {
          window.removeEventListener('online', onOnline);
          if (timer !== undefined) clearTimeout(timer);
          cancelWait = null;
          resolve(go);
        };
        const onOnline = () => finish(true);
        cancelWait = () => finish(false);
        if (navigator.onLine === false) {
          window.addEventListener('online', onOnline);
        } else {
          timer = setTimeout(() => finish(true), RETRY_DELAY_MS);
        }
      });

    // The access token has expired but a refresh token is stored: try to get
    // a new session before calling the user signed out. A rejection (Cognito
    // refused the refresh token) is final -- TokenManager has already cleared
    // the tokens. A transient failure keeps the tokens and is retried exactly
    // once; if that also fails, the session is over.
    const recoverSession = async (): Promise<boolean> => {
      try {
        await tokenManager.refreshTokens();
        return true;
      } catch (error) {
        if (!(error as { transient?: boolean } | null)?.transient) {
          console.error('Session refresh rejected during auth check:', error);
          return false;
        }
        console.warn('Session refresh failed transiently; retrying once:', error);
      }

      // Unmounted during the first refresh: schedule nothing.
      if (cancelled) return false;

      // Offline mode has a snapshot worth showing, so don't hold the page
      // behind the wait: render it now (userless, as before) and let the
      // retry finish in the background. `sessionRecovering` tells consumers
      // (6.0ag's redirect) that the missing user may yet come back.
      const background = sessionStorage.getItem(OFFLINE_MODE_KEY) === 'true';
      if (background) {
        setSessionRecovering(true);
        setLoading(false);
      }

      // `sessionRecovering` is NOT cleared here. On success the user is not
      // back until checkAuthStatus has hydrated the Canvas credentials and
      // called setCurrentUser; clearing the flag earlier committed a render
      // with no user and no recovery in flight, which ProtectedRoute reads as
      // "signed out" (task 6.0ag). checkAuthStatus clears it in the same
      // update as setCurrentUser, or after the tokens are cleared on failure.
      if (!(await waitToRetry())) return false;
      try {
        await tokenManager.refreshTokens();
        return true;
      } catch (error) {
        console.error('Session refresh retry failed:', error);
        // clearTokens notifies 'tokens_cleared', which runs the purge.
        await tokenManager.clearTokens();
        return false;
      }
    };

    const checkAuthStatus = async () => {
      setLoading(true);

      try {
        const tokenInfo = await tokenManager.getTokenInfo();

        if (!tokenInfo.hasValidTokens) {
          const recovered = tokenInfo.refreshToken ? await recoverSession() : false;
          if (cancelled) return;
          if (!recovered) {
            setCurrentUser(null);
            setAvailableUsers([]);
            return;
          }
        }

        tokenManager.startMonitoring();

        const refreshCheck = tokenManager.needsRefresh();
        if (refreshCheck.needed && refreshCheck.reason === 'expiring_soon') {
          try {
            await tokenManager.refreshTokens();
          } catch (error) {
            console.error('Token refresh failed during auth check:', error);
            return; // TokenManager handles logout
          }
        }

        const userEmail = localStorage.getItem('userEmail');
        // Task 6.0ag: restoring a user is a sign-in as far as the offline
        // session is concerned. localStorage is shared across tabs, so this
        // tab's snapshot may belong to someone else than the account whose
        // tokens are here. Before anything can render with the new user.
        discardOfflineSessionUnlessOwner(userEmail);
        let canvas = readCanvasFromSession();

        if (!canvas.canvasToken) {
          canvas = (await hydrateCanvasCredentials()) || canvas;
        } else {
          setAvailableUsers(canvas.availableUsers);
          // Session has a token but no selected user: auto-select the default.
          if (!canvas.canvasUserId) {
            const defaultUser = canvas.availableUsers.find(user => user.default === true);
            if (defaultUser) {
              sessionStorage.setItem('canvasUserId', String(defaultUser.id));
              canvas = { ...canvas, canvasUserId: toCanvasUserId(defaultUser.id) };
            }
          }
        }

        setCurrentUser({ email: userEmail, ...canvas });
      } catch (error) {
        console.error('Auth check error:', error);
        setCurrentUser(null);
        setAvailableUsers([]);
      } finally {
        if (!cancelled) {
          // Batched with the setCurrentUser above on success, so no render
          // sees "not recovering" without the user; a no-op unless the
          // offline background retry set it.
          setSessionRecovering(false);
          setLoading(false);
        }
      }
    };

    checkAuthStatus();

    return () => {
      cancelled = true;
      cancelWait?.();
    };
  }, [hydrateCanvasCredentials]);

  const forceLogout = useCallback(async () => {
    try {
      await tokenManager.forceLogout();
      // `tokenManager.forceLogout` notifies 'force_logout', so the listener
      // above has usually already started a purge. Awaiting one here as
      // well is deliberate: this path is called directly (a failed manual
      // refresh) and must not depend on a listener being mounted. The purge
      // deletes a cache by name, so the second run is a no-op.
      await purgeCachedUserData();
      clearSessionKeepingReturn();
      setCurrentUser(null);
      setAvailableUsers([]);
      return true;
    } catch (error) {
      console.error('Force logout error:', error);
      return false;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      await tokenManager.refreshTokens();
      return true;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      await forceLogout();
      throw error;
    }
  }, [forceLogout]);

  // Register a new user
  const register = useCallback(async (username: string, email: string, password: string) => {
    const signUpResponse = await signUp(username, password, email);

    if (signUpResponse.UserConfirmed === false) {
      return {
        email,
        username,
        needsConfirmation: true,
        message:
          'Registration successful! Please check your email to confirm your account before signing in.',
      };
    }

    // User pool auto-confirms: sign in right away.
    try {
      const signInResponse = await signIn(email, password);
      if ('challengeName' in signInResponse) {
        // A challenge immediately after registration is unexpected; fall back
        // to the manual sign-in path.
        throw new Error('Unexpected authentication challenge after registration');
      }
      await persistSession(signInResponse, email);
      discardOfflineSessionUnlessOwner(email);
      setCurrentUser({ email, username });
      return { email, username, needsConfirmation: false, autoSignedIn: true };
    } catch (signInError) {
      console.error('Auto sign-in failed after registration:', signInError);
      return {
        email,
        username,
        needsConfirmation: true,
        message:
          'Registration successful! Please check your email to confirm your account before signing in.',
      };
    }
  }, []);

  // Log in existing user
  const login = useCallback(
    async (email: string, password: string) => {
      const signInResponse = await signIn(email, password);

      // Challenge responses (e.g. NEW_PASSWORD_REQUIRED) are handled by the UI.
      if ('challengeName' in signInResponse) {
        return {
          success: false,
          challenge: signInResponse,
          message: signInResponse.message,
        };
      }

      await persistSession(signInResponse, email);
      // Task 6.0ag: every sign-in, not only the /login page's, must not hand
      // a previous account's offline snapshot to this one (ConfirmEmail
      // signs in through here without ever visiting /login).
      discardOfflineSessionUnlessOwner(email);
      clearGuestAndDemoState();

      // Server is the source of truth for Canvas credentials; fall back to
      // anything already in this session if the fetch comes back empty.
      const canvas = (await hydrateCanvasCredentials()) || readCanvasFromSession();
      if (canvas.availableUsers.length > 0) {
        setAvailableUsers(canvas.availableUsers);
      }

      setCurrentUser({ email, ...canvas });
      return { email, hasCanvasCredentials: !!canvas.canvasToken };
    },
    [hydrateCanvasCredentials]
  );

  // Complete password challenge (NEW_PASSWORD_REQUIRED)
  const completePasswordChallenge = useCallback(
    async (challengeResponse: PasswordChallengeResponse) => {
      const { email } = challengeResponse;

      await persistSession(challengeResponse, email);
      discardOfflineSessionUnlessOwner(email);
      clearGuestAndDemoState();

      const canvas = (await hydrateCanvasCredentials()) || readCanvasFromSession();
      setCurrentUser({ email, ...canvas });

      return { success: true, email, hasCanvasCredentials: !!canvas.canvasToken };
    },
    [hydrateCanvasCredentials]
  );

  // Store/replace the user's Canvas credentials (server + session cache).
  const updateUserCanvasCredentials = useCallback(
    async (
      token: string,
      userId: string | number | null,
      rootUrl: string,
      users: CanvasUser[] = []
    ) => {
      await storeCanvasCredentials(token, userId != null ? String(userId) : '', rootUrl);

      const availableUsersList: CanvasUser[] =
        users.length > 0 ? users : currentUser?.availableUsers || [];

      // Session cache of what's stored securely on the server.
      sessionStorage.setItem('canvasToken', token);
      sessionStorage.setItem('canvasRootUrl', rootUrl);
      if (userId != null) {
        sessionStorage.setItem('canvasUserId', String(userId));
      }
      if (availableUsersList.length > 0) {
        sessionStorage.setItem('availableUsers', JSON.stringify(availableUsersList));
      }

      setAvailableUsers(availableUsersList);
      setCurrentUser((prev) => ({
        ...prev,
        canvasToken: token,
        canvasUserId: userId != null ? toCanvasUserId(userId) : prev?.canvasUserId,
        canvasRootUrl: rootUrl,
        availableUsers: availableUsersList,
      }));

      return true;
    },
    [currentUser]
  );

  // Switch to a different Canvas user
  const switchCanvasUser = useCallback(
    async (userId: string | number) => {
      if (!currentUser?.canvasToken) {
        throw new Error('No Canvas token available');
      }

      // sessionStorage and React state must agree on the spelling: the
      // pre-5.2c code wrote String(userId) to one and the raw argument to
      // the other, which is how one child ended up with two query keys
      // reachable in a single session (switch, then refresh).
      sessionStorage.setItem('canvasUserId', String(userId));
      setCurrentUser((prev) => ({ ...prev, canvasUserId: toCanvasUserId(userId) }));
      return true;
    },
    [currentUser]
  );

  // Refresh available users from sessionStorage
  const refreshAvailableUsers = useCallback(() => {
    const users = parseCanvasUsers(sessionStorage.getItem('availableUsers'));
    if (users.length > 0) {
      setAvailableUsers(users);
    }
  }, []);

  // Log out
  const logout = useCallback(async () => {
    try {
      tokenManager.stopMonitoring();
      await tokenManager.clearTokens();
      // Awaited, and awaited *here*: Header/Sidebar/Profile all `await
      // logout()` and then navigate. Ordering the purge inside logout is
      // what stops the navigation from racing it -- a posted-but-unhandled
      // purge message leaves the cache intact and says nothing about it.
      await purgeCachedUserData();
      sessionStorage.clear();
      setCurrentUser(null);
      setAvailableUsers([]);
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  }, []);

  // Send a password-reset code to the user's email
  const resetPassword = useCallback(async (email: string) => {
    await forgotPassword(email);
    return true;
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (_user: AuthUser | null, profileData: ProfileUpdateData) => {
    if (profileData.displayName) {
      localStorage.setItem('userName', profileData.displayName);
    }
    if (profileData.photoURL) {
      localStorage.setItem('userPhoto', profileData.photoURL);
    }

    setCurrentUser((prevUser) => ({ ...prevUser, ...profileData }));
    return true;
  }, []);

  // Update password
  const updatePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const accessToken = tokenManager.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available. Please log in again.');
    }

    await changePassword(accessToken, currentPassword, newPassword);
    return true;
  }, []);

  // Permanently delete the user's account (Cognito user + local state).
  const deleteAccount = useCallback(async () => {
    const accessToken = tokenManager.getAccessToken();
    if (!accessToken) {
      throw new Error('No active session. Please sign in again before deleting your account.');
    }

    await deleteUser(accessToken);

    tokenManager.stopMonitoring();
    await tokenManager.clearTokens();
    // The Cognito user is gone; their cached course and grade data must not
    // outlive the account it belonged to.
    await purgeCachedUserData();
    sessionStorage.clear();
    setCurrentUser(null);
    setAvailableUsers([]);
    return true;
  }, []);

  // Check if user has Canvas credentials set up
  const hasCanvasCredentials = useCallback(() => {
    // Guest mode keeps its credentials in sessionStorage.
    const isGuest = !!sessionStorage.getItem('isGuestMode');
    if (isGuest) {
      try {
        const credentials = JSON.parse(sessionStorage.getItem('guestCredentials') || 'null');
        if (credentials?.token && credentials?.userId) {
          return true;
        }
      } catch {
        // fall through to authenticated checks
      }
    }

    if (currentUser?.canvasToken) {
      return true;
    }

    // Fallback for the window before currentUser is fully hydrated.
    return !!sessionStorage.getItem('canvasToken');
  }, [currentUser]);

  const confirmRegistration = useCallback(async (username: string, confirmationCode: string) => {
    await confirmSignUp(username, confirmationCode);
    return {
      success: true,
      message: 'Account confirmed successfully! You can now sign in with your credentials.',
    };
  }, []);

  const resendConfirmationCode = useCallback(async (email: string) => {
    await resendSignUp(email);
    return { success: true, message: 'Confirmation code sent successfully!' };
  }, []);

  const isGuestMode = useCallback(() => {
    return !!sessionStorage.getItem('isGuestMode');
  }, []);

  // Context value - memoized to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      currentUser,
      availableUsers,
      loading,
      sessionRecovering,
      register,
      confirmRegistration,
      resendConfirmationCode,
      login,
      completePasswordChallenge,
      logout,
      forceLogout,
      refreshToken,
      resetPassword,
      updateProfile,
      updatePassword,
      deleteAccount,
      updateUserCanvasCredentials,
      switchCanvasUser,
      refreshAvailableUsers,
      hasCanvasCredentials,
      isGuestMode,
    }),
    [
      currentUser,
      availableUsers,
      loading,
      sessionRecovering,
      register,
      confirmRegistration,
      resendConfirmationCode,
      login,
      completePasswordChallenge,
      logout,
      forceLogout,
      refreshToken,
      resetPassword,
      updateProfile,
      updatePassword,
      deleteAccount,
      updateUserCanvasCredentials,
      switchCanvasUser,
      refreshAvailableUsers,
      hasCanvasCredentials,
      isGuestMode,
    ]
  );

  // Props are spelled out because TypeScript infers Loading's defaulted props
  // from its .jsx source as required.
  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <Loading message="Loading..." size="medium" overlay={false} transparent={false} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
